import { useState, FormEvent } from "react";

const interests = [
  { id: "delta-2", label: "DELTA 2", icon: "ri-flashlight-line" },
  { id: "delta-pro", label: "DELTA Pro", icon: "ri-battery-charge-line" },
  { id: "river-2-pro", label: "RIVER 2 Pro", icon: "ri-drop-line" },
  { id: "delta-pro-ultra", label: "DELTA Pro Ultra", icon: "ri-home-wifi-line" },
  { id: "smart-home-panel", label: "Smart Home Panel", icon: "ri-settings-3-line" },
  { id: "solar-panels", label: "Solar Panels", icon: "ri-sun-line" },
];

const useCases = [
  "Home backup power",
  "Off-grid living",
  "Camping & RV",
  "Emergency preparedness",
  "Solar energy storage",
  "Job site power",
];

export default function EcoFlowSignup() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    zipCode: "",
    message: "",
  });
  const [selectedInterests, setSelectedInterests] = useState<string[]>([]);
  const [selectedUseCases, setSelectedUseCases] = useState<string[]>([]);
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");

  const toggleInterest = (id: string) => {
    setSelectedInterests((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const toggleUseCase = (uc: string) => {
    setSelectedUseCases((prev) =>
      prev.includes(uc) ? prev.filter((u) => u !== uc) : [...prev, uc]
    );
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!form.email.trim() || !form.name.trim()) return;
    setStatus("submitting");

    try {
      const body = new URLSearchParams();
      body.append("name", form.name.trim());
      body.append("email", form.email.trim());
      if (form.phone.trim()) body.append("phone", form.phone.trim());
      if (form.zipCode.trim()) body.append("zipCode", form.zipCode.trim());
      if (form.message.trim()) body.append("message", form.message.trim());
      body.append("interestedProducts", selectedInterests.join(", ") || "Not specified");
      body.append("useCases", selectedUseCases.join(", ") || "Not specified");
      body.append("source", "EcoFlow Landing Page — Early Access Form");

      const res = await fetch("https://readdy.ai/api/form/d77qpsadqn9mib2tqca0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      if (res.ok) {
        setStatus("success");
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="ecoflow-signup" className="py-20 px-4 md:px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left — copy & benefits */}
          <div>
            <span className="inline-block text-amber-500 text-xs font-bold uppercase tracking-widest mb-3">Early Access</span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 tracking-tight mb-5">
              Be First When<br />EcoFlow Goes Live
            </h2>
            <p className="text-gray-500 text-base leading-relaxed mb-8">
              Sign up now and we&apos;ll notify you the moment EcoFlow products are available on Iron Haven Supply — plus you&apos;ll get first access to any launch promotions, bundle deals, and authorized dealer pricing.
            </p>

            {/* Benefits list */}
            <div className="space-y-4 mb-10">
              {[
                {
                  icon: "ri-price-tag-3-line",
                  title: "Launch Pricing Alerts",
                  desc: "Get notified of any introductory pricing or bundle deals before they go public.",
                },
                {
                  icon: "ri-shield-check-line",
                  title: "Authorized Dealer Guarantee",
                  desc: "Every EcoFlow product comes with full manufacturer warranty support.",
                },
                {
                  icon: "ri-customer-service-2-line",
                  title: "Expert Pre-Sales Support",
                  desc: "Our team will help you choose the right system for your home before you buy.",
                },
                {
                  icon: "ri-gift-line",
                  title: "Exclusive Early Access Perks",
                  desc: "Early signups may receive exclusive bundle offers not available to the general public.",
                },
              ].map((b) => (
                <div key={b.title} className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-amber-50 rounded-xl shrink-0">
                    <i className={`${b.icon} text-amber-500 text-lg`}></i>
                  </div>
                  <div>
                    <p className="text-gray-900 font-bold text-sm">{b.title}</p>
                    <p className="text-gray-500 text-xs mt-0.5 leading-relaxed">{b.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Social proof */}
            <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
              <div className="flex items-center gap-3 mb-3">
                <div className="flex -space-x-2">
                  {[1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      className="w-8 h-8 rounded-full border-2 border-white bg-amber-100 flex items-center justify-center"
                    >
                      <i className="ri-user-line text-amber-600 text-xs"></i>
                    </div>
                  ))}
                </div>
                <div>
                  <p className="text-gray-900 font-bold text-sm">340+ already signed up</p>
                  <p className="text-gray-400 text-xs">from the homepage and brands page</p>
                </div>
              </div>
              <p className="text-gray-500 text-xs italic">
                &ldquo;Finally! I&apos;ve been waiting for an authorized EcoFlow dealer with real support. Can&apos;t wait for launch.&rdquo;
              </p>
              <p className="text-gray-400 text-[10px] mt-1">— Early signup from Pittsburgh, PA</p>
            </div>
          </div>

          {/* Right — form */}
          <div>
            {status === "success" ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-10 text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-emerald-100 rounded-full mx-auto mb-5">
                  <i className="ri-check-line text-emerald-500 text-3xl"></i>
                </div>
                <h3 className="text-gray-900 font-extrabold text-xl mb-2">You&apos;re on the list!</h3>
                <p className="text-gray-500 text-sm leading-relaxed max-w-sm mx-auto">
                  We&apos;ll send you one email the moment EcoFlow goes live on Iron Haven Supply — plus any early access deals we can offer.
                </p>
                <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                  <a
                    href="/shop"
                    className="inline-flex items-center justify-center gap-2 bg-gray-900 hover:bg-gray-800 text-white text-sm font-semibold px-6 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Browse Current Products
                  </a>
                  <a
                    href="/authorized-brands"
                    className="inline-flex items-center justify-center gap-2 bg-white border border-gray-200 hover:border-amber-300 text-gray-700 text-sm font-semibold px-6 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    View Authorized Brands
                  </a>
                </div>
              </div>
            ) : (
              <form
                data-readdy-form
                id="ecoflow-landing-signup"
                onSubmit={handleSubmit}
                className="bg-white border border-gray-100 rounded-2xl p-7 md:p-8"
              >
                <h3 className="text-gray-900 font-extrabold text-xl mb-1">Get Early Access</h3>
                <p className="text-gray-400 text-sm mb-6">Takes 30 seconds. No spam, ever.</p>

                {/* Name + Email */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-gray-700 text-xs font-semibold mb-1.5">
                      Full Name <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={form.name}
                      onChange={handleChange}
                      placeholder="John Smith"
                      className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-amber-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-xs font-semibold mb-1.5">
                      Email Address <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={form.email}
                      onChange={handleChange}
                      placeholder="john@example.com"
                      className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-amber-400 transition-colors"
                    />
                  </div>
                </div>

                {/* Phone + Zip */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
                  <div>
                    <label className="block text-gray-700 text-xs font-semibold mb-1.5">Phone (optional)</label>
                    <input
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="(555) 000-0000"
                      className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-amber-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-xs font-semibold mb-1.5">ZIP Code (optional)</label>
                    <input
                      type="text"
                      name="zipCode"
                      value={form.zipCode}
                      onChange={handleChange}
                      placeholder="15102"
                      className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-amber-400 transition-colors"
                    />
                  </div>
                </div>

                {/* Products of interest */}
                <div className="mb-5">
                  <label className="block text-gray-700 text-xs font-semibold mb-2">
                    Which products interest you? (select all that apply)
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {interests.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => toggleInterest(item.id)}
                        className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border transition-all cursor-pointer whitespace-nowrap ${
                          selectedInterests.includes(item.id)
                            ? "bg-amber-500 border-amber-500 text-white"
                            : "bg-white border-gray-200 text-gray-600 hover:border-amber-300"
                        }`}
                      >
                        <div className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className={`${item.icon} text-xs`}></i>
                        </div>
                        {item.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Use cases */}
                <div className="mb-5">
                  <label className="block text-gray-700 text-xs font-semibold mb-2">
                    What&apos;s your primary use case?
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {useCases.map((uc) => (
                      <button
                        key={uc}
                        type="button"
                        onClick={() => toggleUseCase(uc)}
                        className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-all cursor-pointer whitespace-nowrap ${
                          selectedUseCases.includes(uc)
                            ? "bg-gray-900 border-gray-900 text-white"
                            : "bg-white border-gray-200 text-gray-600 hover:border-gray-400"
                        }`}
                      >
                        {uc}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Message */}
                <div className="mb-6">
                  <label className="block text-gray-700 text-xs font-semibold mb-1.5">
                    Anything specific you&apos;d like us to know? (optional)
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    placeholder="e.g. I need a system that can power my sump pump and fridge during outages..."
                    rows={3}
                    maxLength={500}
                    className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-amber-400 transition-colors resize-none"
                  />
                  <p className="text-gray-400 text-[10px] mt-1 text-right">{form.message.length}/500</p>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="w-full flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-white font-bold text-sm py-3.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  {status === "submitting" ? (
                    <>
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-loader-4-line animate-spin text-sm"></i>
                      </div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-bell-line text-sm"></i>
                      </div>
                      Notify Me at Launch
                    </>
                  )}
                </button>

                {status === "error" && (
                  <p className="text-red-500 text-xs mt-3 text-center flex items-center justify-center gap-1.5">
                    <i className="ri-error-warning-line"></i>
                    Something went wrong. Please try again or{" "}
                    <a href="/contact" className="underline">contact us</a>.
                  </p>
                )}

                <p className="text-gray-400 text-[10px] text-center mt-3">
                  No spam. One email when EcoFlow launches. Unsubscribe anytime.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
